import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import ProductCard from './ProductCard';
import ProductsLoading from './ProductsLoading';
import ProductsError from './ProductsError';
import NoProductsFound from './NoProductsFound';

const ProductsSection = ({
  products,
  filteredProducts,
  loading,
  error,
  resetFilters,
  hasMore,
  loadingMore,
  loadMoreProducts,
  loadMoreError,
  retryLoadMore,
  activeCategoryName,
  onShowAll
}) => {
  const productsSectionRef = useRef(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const node = sentinelRef.current;

    if (!node || loading || error || !hasMore || typeof loadMoreProducts !== 'function') {
      return undefined;
    }

    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingMore) {
        loadMoreProducts();
      }
    }, { rootMargin: '250px 0px' });

    observer.observe(node);

    return () => observer.disconnect();
  }, [error, hasMore, loadMoreProducts, loading, loadingMore]);

  return (
    <div id="products" ref={productsSectionRef} className="scroll-mt-28 bg-white py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12 flex flex-col items-center gap-6 text-center sm:flex-row sm:items-end sm:justify-between sm:text-left">
          <div>
            <span className="inline-flex items-center gap-3 text-[0.68rem] font-semibold uppercase tracking-[0.3em] text-[#3d8361]">
              <span className="h-px w-8 bg-[#3d8361]/50" />
              La boutique
            </span>
            <h2 className="mb-0 mt-5 text-center font-serif text-4xl font-medium text-[#0c2a27] sm:text-left sm:text-5xl">
              {activeCategoryName || 'Notre collection'}
            </h2>
          </div>
          {activeCategoryName && typeof onShowAll === 'function' && (
            <button
              type="button"
              onClick={onShowAll}
              className="inline-flex items-center gap-2 rounded-full border border-[#0c2a27]/15 px-5 py-2.5 text-sm font-medium text-[#0c2a27] transition-colors hover:border-[#0c2a27]/40 hover:bg-[#faf7f2]"
            >
              Voir tous les produits
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="lg:grid lg:grid-cols-4 lg:gap-8">
          {/* Products grid */}
          <div className="mt-6 lg:mt-0 lg:col-span-4">
            {loading ? (
              <ProductsLoading />
            ) : error ? (
              <ProductsError error={error} />
            ) : filteredProducts.length > 0 ? (
              <>
                <div className="grid grid-cols-1 gap-y-10 gap-x-6 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>

                {loadingMore && (
                  <div className="mt-10 flex items-center justify-center">
                    <div className="inline-flex items-center gap-3 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm text-gray-600 shadow-sm">
                      <span className="h-3 w-3 animate-spin rounded-full border-2 border-[#174540] border-t-transparent" />
                      Chargement de plus de produits...
                    </div>
                  </div>
                )}

                {loadMoreError && (
                  <div className="mt-10 flex items-center justify-between gap-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    <span>{loadMoreError}</span>
                    <button
                      onClick={retryLoadMore}
                      className="rounded-full bg-[#174540] px-4 py-2 text-white transition-colors hover:bg-[#0f2f2b]"
                    >
                      Réessayer
                    </button>
                  </div>
                )}

                <div ref={sentinelRef} className="h-1 w-full" aria-hidden="true" />
              </>
            ) : (
              <NoProductsFound resetFilters={resetFilters} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductsSection;